import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

export interface DashboardResultadosRequest {
  userId: number;
  dataInicio: string;
  dataFim: string;
  ordenacaoProdutos: string;
  ordenacaoClientes: string;
  ordenacaoCidades: string;
  tipoDados: string;
  codTipopedvenda?: string;
  vendedor: string;
  regiao?: string;
  cliente: string;
  produto: string;
  grupo?: string;
  grupo2?: string;
  grupo3?: string;
  grupos: string[];
  grupos2: string[];
  grupos3: string[];
  sellerId?: number;
  clienteId?: number;
  produtoId?: number;
}

export interface DashboardResultadosReativacao {
  regiao: string;
  clientId: number;
  codigoErp: string;
  cliente: string;
  sellerId: number;
  vendedor: string;
  ultimaCompra: string | null;
  reativacao: string;
  diasSemCompra: number;
  volume: number;
  valor: number;
}

export interface DashboardResultadosReativacoes {
  regioes: string[];
  totalClientes: number;
  volumeTotal: number;
  valorTotal: number;
  clientes: DashboardResultadosReativacao[];
  vendedores: { id: number; fullname: string }[];
}

export interface SankhyaMetaLinha {
  referencia: string; codvend: number; vendedor: string; supervisor: string; regiao: string;
  codtipo: number; indicador: string; objetivo: number | null; realizado: number | null; percentual: number | null;
}
export interface SankhyaMetasResultado { consultadoEm: string; linhas: SankhyaMetaLinha[]; }

export interface DashboardResultadosDia {
  dia: string;
  vendasReais: number;
  vendasLitros: number;
  pedidos: number;
  visitas: number;
  ticketMedio: number;
}

export interface DashboardResultadosProduto {
  posicao: number;
  produto: string;
  quantidade: number;
  litros: number;
  vendasReais: number;
}

export interface DashboardResultadosCliente {
  posicao: number;
  cliente: string;
  documento: string;
  quantidade: number;
  litros: number;
  vendasReais: number;
}

export interface DashboardResultadosCidade {
  posicao: number;
  cidade: string;
  quantidade: number;
  litros: number;
  vendasReais: number;
}

export interface DashboardResultadosVendasCanal {
  sellerId: number;
  vendedor: string;
  vendasExternas: number;
  vendasInternas: number;
  total: number;
}

export interface DashboardResultadosPositivacaoCliente {
  sellerId: number;
  vendedor: string;
  totalClientes: number;
  positivacao: number;
  percentual: number;
}

export interface DashboardResultadosAberturaClienteDetalhe {
  clientId: number;
  cliente: string;
  codigoErp: string;
  dataCompra: string;
  dataCompraAnterior?: string;
  numeroPedido: number;
  numeroNf: string;
  valorFaturado: number;
}

export interface DashboardResultadosAberturaCliente {
  sellerId: number;
  vendedor: string;
  totalClientes: number;
  valorFaturado: number;
  clientes: DashboardResultadosAberturaClienteDetalhe[];
}

export interface DashboardResultadosMetaDetalhe {
  sellerId: number;
  vendedor: string;
  descricao: string;
  objetivo: number;
  realizado: number;
  percentual: number;
}

export interface DashboardResultadosMeta {
  codigo: string;
  descricao: string;
  tipo: string;
  localTipo: string;
  objetivo: number;
  realizado: number;
  percentual: number;
  status: string;
  detalhes: DashboardResultadosMetaDetalhe[];
}

export interface DashboardResultadosResumo {
  dias: DashboardResultadosDia[];
  produtos: DashboardResultadosProduto[];
  clientes: DashboardResultadosCliente[];
  cidades: DashboardResultadosCidade[];
  vendasCanais: DashboardResultadosVendasCanal[];
  positivacaoClientes: DashboardResultadosPositivacaoCliente[];
  positivacaoClientesAtivos: DashboardResultadosPositivacaoCliente[];
  aberturaClientes: DashboardResultadosAberturaCliente[];
  metas: DashboardResultadosMeta[];
}

export interface DashboardResultadosFiltroGrupos {
  tiposPedidos?: string[];
  grupos: string[];
  grupos2: string[];
  grupos3: string[];
  relacoes: DashboardResultadosFiltroGrupoRelacao[];
}

export interface DashboardResultadosFiltroGrupoRelacao {
  grupo: string;
  grupo2: string;
  grupo3: string;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardResultadosService {
  public getReativacoes(payload: DashboardResultadosRequest): Observable<DashboardResultadosReativacoes> {
    return this.http.post<DashboardResultadosReativacoes>(this.baseUrl + 'api/DashboardResultados/GetReativacoes', payload, {
      headers: { 'X-Login-Diario': localStorage.getItem('login_diario_token') || '' }
    });
  }
  public consultarMetasSankhya(userId: number, mesReferencia: string): Observable<SankhyaMetasResultado> {
    return this.http.post<SankhyaMetasResultado>(this.baseUrl + 'api/DashboardResultados/ConsultarMetasSankhya', { userId, mesReferencia });
  }
  constructor(
    private http: HttpClient,
    @Inject('BASE_URL') private baseUrl: string,
    private toastr: ToastrService
  ) { }

  public getResumo(payload: DashboardResultadosRequest): Observable<DashboardResultadosResumo> {
    return this.http
      .post<DashboardResultadosResumo>(this.baseUrl + 'api/DashboardResultados/GetResumo', payload)
      .pipe(
        catchError(() => {
          this.toastr.error('', 'Erro ao carregar Dashboard Resultados. Contate o suporte...');
          return of({ dias: [], produtos: [], clientes: [], cidades: [], vendasCanais: [], positivacaoClientes: [], positivacaoClientesAtivos: [], aberturaClientes: [], metas: [] });
        })
      );
  }

  public getFiltroGrupos(payload: DashboardResultadosRequest): Observable<DashboardResultadosFiltroGrupos> {
    return this.http
      .post<DashboardResultadosFiltroGrupos>(this.baseUrl + 'api/DashboardResultados/GetFiltroGrupos', payload)
      .pipe(
        catchError(() => {
          this.toastr.error('', 'Erro ao carregar filtros de grupos. Contate o suporte...');
          return of({ grupos: [], grupos2: [], grupos3: [], relacoes: [] });
        })
      );
  }

  public getAberturaClientes(payload: DashboardResultadosRequest): Observable<DashboardResultadosAberturaCliente[]> {
    return this.http
      .post<DashboardResultadosAberturaCliente[]>(this.baseUrl + 'api/DashboardResultados/GetAberturaClientes', payload)
      .pipe(
        catchError(() => {
          this.toastr.error('', 'Erro ao carregar abertura de clientes com compra. Contate o suporte...');
          return of([]);
        })
      );
  }
}
